import * as React from 'react';
import { Box, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { expandShortHex } from '../../utility/preferenceColor';
import { useEffect } from 'react';
import { DEFAULT_PREFERENCE_COLOR } from '../../types/Preference';

const HEX3 = /^#[0-9a-f]{3}$/i;
const HEX6 = /^#[0-9a-f]{6}$/i;

type Props = {
  value?: string; // '#abc' or '#aabbcc'
  onChange: (hex?: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
};

export default function PreferenceColorField({
  value,
  onChange,
  label = 'Color',
  helperText = 'Pick a color or enter a hex like #aabbcc',
  disabled,
  size = 'medium',
}: Props) {
  const [text, setText] = React.useState<string>(value ?? '');
  const colorInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if ((value ?? '') !== text) setText(value ?? '');
  }, [value]);

  const lower = text.toLowerCase();
  const isValid = text === '' || HEX3.test(lower) || HEX6.test(lower);

  const preview = expandShortHex(isValid && text ? lower : undefined) ?? DEFAULT_PREFERENCE_COLOR;
  const previewBorder = '1px solid rgba(0,0,0,0.2)';

  const colorPickerValue = expandShortHex(HEX3.test(lower) || HEX6.test(lower) ? lower : DEFAULT_PREFERENCE_COLOR)!;

  const commitIfValid = (maybe: string) => {
    const v = maybe.toLowerCase();
    if (v === '') {
      onChange(undefined);
    } else if (HEX3.test(v) || HEX6.test(v)) {
      onChange(v);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    commitIfValid(next);
  };

  const handleBlur = () => {
    const trimmed = text.trim().toLowerCase();
    if (trimmed !== text) {
      setText(trimmed);
      commitIfValid(trimmed);
    }
  };

  const openPicker = () => {
    if (disabled) return;
    colorInputRef.current?.click();
  };

  const onSwatchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex6 = e.target.value.toLowerCase();
    setText(hex6);
    onChange(hex6);
  };

  return (
    <FormControl disabled={disabled} fullWidth>
      <Typography variant="caption" sx={{ mb: 0.5 }}>
        {label}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box
          role="button"
          aria-label="Open color picker"
          tabIndex={disabled ? -1 : 0}
          onClick={openPicker}
          onKeyDown={onSwatchKey}
          sx={{
            width: 36,
            height: 28,
            borderRadius: 0.75,
            bgcolor: preview,
            border: previewBorder,
            flex: '0 0 36px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            outline: 'none',
            '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}55` },
          }}
        />

        <input
          ref={colorInputRef}
          type="color"
          value={colorPickerValue}
          onChange={handlePickerChange}
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
          aria-hidden
          tabIndex={-1}
          disabled={disabled}
        />

        <TextField
          value={text}
          onChange={handleInput}
          onBlur={handleBlur}
          error={text !== '' && !isValid}
          placeholder="#aabbcc"
          size={size}
          slotProps={{ input: { inputProps: { maxLength: 7 } } }}
          sx={{ flex: 1 }}
        />
      </Box>

      {!isValid && text !== '' ? (
        <FormHelperText error>Invalid hex (e.g., #1a2b3c)</FormHelperText>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}
